import { ApiTranslation } from '../api-translation';
import { CurrencyView } from '../currency-view';
import { SpanLoading } from '../fetch-status/span-loading';
import { TextTooltip } from '../text-tooltip';
import { UploadedImage } from '../uploaded-image';
import { resolveBinaryOptions } from './resolve-binary-options';
import { InteractRefreshIcon, ViewCheckmarkIcon, ViewChevronDownIcon } from '@/client/assets';
import { CTF_ADDRESS } from '@/client/confs/app';
import { useOnChainPosition } from '@/client/hooks/use-on-chain';
import { useSignedUserInfo } from '@/client/hooks/use-signed-user-info';
import { OptionSide } from '@/client/store/trade-store';
import { cn } from '@/client/utils';
import { api } from '@/client/utils/api';
import { getDefaultImg } from '@/client/utils/markdown';
import { useUserPositions } from '@/libs/react-sdk/api/use-user-options';
import { MarketDetail } from '@/libs/sdk/api';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Address } from 'viem';

type TreadOptionsProps = {
  className?: string;
  type: 'buy' | 'sell';
  market: MarketDetail;
  optionAddress?: string;
  setOptionAddress?: (optionAddress: string) => void;
  optionSide?: OptionSide;
  setOptionSide?: (optionSide?: OptionSide) => void;
  uid: string;
  /** On-chain balance for the currently selected option (from useBalance) */
  onChainBalance?: bigint;
  onChainBalanceLoading?: boolean;
};

export const TreadOptions = ({
  type,
  market,
  optionAddress,
  uid,
  setOptionAddress,
  optionSide,
  setOptionSide,
  onChainBalance,
}: TreadOptionsProps) => {
  const { t } = useTranslation();
  // Name match first (Yes/No), index fallback for custom binaries (Up/Down)
  const { yes, no } = useMemo(() => resolveBinaryOptions(market.optionList), [market.optionList]);

  // On-chain CTF balances for both yes/no options (binary sell needs both)
  const { data: userInfo } = useSignedUserInfo(api);
  const walletAddress = userInfo?.eoaAddress as Address | undefined;
  const yesPositionId = useMemo(() => (yes?.positionId ? BigInt(yes.positionId) : undefined), [yes?.positionId]);
  const noPositionId = useMemo(() => (no?.positionId ? BigInt(no.positionId) : undefined), [no?.positionId]);
  const isBinarySell = type === 'sell' && market.optionList.length <= 2;
  const yesOnChain = useOnChainPosition(
    isBinarySell ? (CTF_ADDRESS as Address | undefined) : undefined,
    walletAddress,
    yesPositionId
  );
  const noOnChain = useOnChainPosition(
    isBinarySell ? (CTF_ADDRESS as Address | undefined) : undefined,
    walletAddress,
    noPositionId
  );

  // Check if this is a multi-option market (more than 2 options)
  const isMultiOption = market.optionList.length > 2;

  const params = useMemo(() => {
    return {
      page: 1,
      pageSize: market.optionList.length,
      marketAddress: market.marketAddress,
      uid,
    };
  }, [market.marketAddress, market.optionList.length, uid]);
  const userPositions = useUserPositions(api, params);
  const positions = useMemo(
    () => userPositions.data?.pages.flatMap(item => item.positions) || [],
    [userPositions.data?.pages]
  );
  const allOptionsWithBalance = useMemo(() => {
    return market.optionList.map(option => {
      const balance =
        positions.find(({ optionAddress }) => optionAddress.toLowerCase() === option.optionAddress.toLowerCase())?.balance ??
        0;
      const optionPicUrl = getDefaultImg(option.description) || option.optionPicUrl;
      return { ...option, balance, optionPicUrl };
    });
  }, [market.optionList, positions]);
  const optionList = useMemo(() => {
    if (type == 'sell' && isMultiOption) {
      return allOptionsWithBalance.filter(item => Number(item.balance) > 0);
    }
    return allOptionsWithBalance;
  }, [allOptionsWithBalance, type, isMultiOption]);
  const currentOption = useMemo(
    () => optionList.find(item => item.optionAddress.toLowerCase() === optionAddress?.toLowerCase()),
    [optionList, optionAddress]
  );

  useEffect(() => {
    // Auto-select first option for multi-option markets (both buy and sell)
    if (isMultiOption && optionAddress == undefined && optionList.length > 0) {
      setOptionAddress?.(optionList[0].optionAddress);
    }
  }, [optionAddress, optionList, setOptionAddress, type, isMultiOption]);

  useEffect(() => {
    // Auto-select YES when a multi-option is selected
    if (type === 'buy' && isMultiOption && optionAddress && optionSide === undefined) {
      setOptionSide?.('yes');
    }
  }, [type, isMultiOption, optionAddress, optionSide, setOptionSide]);

  useEffect(() => {
    // Auto-select for binary sell: pick the option with balance > 0
    if (type === 'sell' && !isMultiOption && optionAddress == undefined && yes && no) {
      const yesOpt = allOptionsWithBalance.find(o => o.optionAddress.toLowerCase() === yes.optionAddress.toLowerCase());
      const noOpt = allOptionsWithBalance.find(o => o.optionAddress.toLowerCase() === no.optionAddress.toLowerCase());
      if (Number(yesOpt?.balance) > 0) setOptionAddress?.(yes.optionAddress);
      else if (Number(noOpt?.balance) > 0) setOptionAddress?.(no.optionAddress);
    }
  }, [type, isMultiOption, optionAddress, yes, no, allOptionsWithBalance, setOptionAddress]);
  return (
    <div className="w-full space-y-2">
      <div className="flex items-center justify-between text-foreground/70">
        <div className="text-sm leading-5">{type == 'buy' ? t('outcome') : t('position')}</div>
        <button
          className="size-4 overflow-hidden"
          disabled={userPositions.isFetching}
          onClick={() => {
            userPositions.refetch();
          }}
        >
          <InteractRefreshIcon
            className={cn(
              'size-4 duration-200 fill-foreground/70 hover:rotate-90',
              userPositions.isFetching && 'animate-spin '
            )}
          />
        </button>
      </div>
      {/* option select - for multi-option markets (both buy and sell) */}
      {isMultiOption && (
        <div>
          <Listbox value={optionAddress} onChange={setOptionAddress}>
            <ListboxButton
              disabled={optionList.length == 0}
              className={cn(
                'flex min-h-14 py-2 px-4 text-xs pr-4 items-center w-full border border-foreground/10 hover:bg-foreground/10 rounded-3 disabled:bg-foreground/5 disabled:text-foreground/10',
                optionList.length == 0 ? 'justify-center' : 'justify-between',
                'data-[active]:bg-foreground/10'
              )}
            >
              {userPositions.isLoading && <SpanLoading />}
              {optionList.length != 0 && !currentOption && t('select-a-outcome')}
              {type == 'sell' && optionList.length == 0 && t('no-available-positions')}
              {currentOption && (
                <div className="flex space-x-3 items-center">
                  <UploadedImage fileKey={currentOption.optionPicUrl} className="size-10 rounded-1" />
                  <div className="space-y-1">
                    <TextTooltip
                      tooltip={currentOption.description}
                      asChild
                      className="line-clamp-1 text-sm text-left flex-1"
                    >
                      <ApiTranslation>{currentOption.description}</ApiTranslation>
                    </TextTooltip>
                    {type === 'buy' && isMultiOption && currentOption && (
                      <div className="text-xs space-x-1 text-left text-foreground/70">
                        <CurrencyView
                          isLoading={userPositions.isLoading}
                          value={currentOption?.price}
                          decimal={currentOption.decimal}
                          precision={2}
                        />
                        <span>{t('chance', 'chance')}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {optionList.length != 0 && <ViewChevronDownIcon className="size-4 fill-current" />}
            </ListboxButton>
            <ListboxOptions
              anchor="bottom start"
              className="w-[var(--button-width)] shadow-xl shadow-foreground/20 mt-2 z-50 bg-card p-2 rounded-3 border border-foreground/10 divide-y-1 divide-foreground/10 cursor-pointer"
            >
              {optionList.map(option => (
                <ListboxOption
                  key={`option-${option.optionAddress}`}
                  value={option.optionAddress}
                  className={cn(
                    'flex space-x-3 text-sm p-2 items-center hover:bg-foreground/5 px-2 rounded-3',
                    optionAddress === option.optionAddress && 'bg-foreground/5'
                  )}
                >
                  <UploadedImage fileKey={option.optionPicUrl} className="size-10 rounded-1" />
                  <div className="space-y-1 flex-1">
                    <TextTooltip tooltip={option.description} className="line-clamp-1 text-left flex-1">
                      <ApiTranslation>{option.description}</ApiTranslation>
                    </TextTooltip>
                    {type === 'sell' && (
                      <span className="text-xs space-x-1">
                        <CurrencyView
                          isLoading={userPositions.isLoading}
                          value={
                            optionAddress === option.optionAddress && onChainBalance !== undefined
                              ? onChainBalance
                              : option?.balance
                          }
                          decimal={option.decimal}
                        />
                        <span>{t('shares')}</span>
                      </span>
                    )}
                    {type === 'buy' && isMultiOption && (
                      <span className="text-xs space-x-1 text-foreground/70">
                        <CurrencyView
                          isLoading={userPositions.isLoading}
                          value={option?.price}
                          decimal={option.decimal}
                          precision={2}
                        />
                        <span>{t('chance', 'chance')}</span>
                      </span>
                    )}
                  </div>
                  {optionAddress === option.optionAddress && <ViewCheckmarkIcon className="size-5" />}
                </ListboxOption>
              ))}
            </ListboxOptions>
          </Listbox>
        </div>
      )}
      {/* Sell YES/NO buttons for multi-option market */}
      {type == 'sell' && isMultiOption && currentOption && (
        <div className="flex items-end justify-between flex-1 gap-3 pt-2">
          <button
            type="button"
            className={cn('px-4 py-3 flex-1 rounded-2 min-h-12 font-semibold leading-none', 'bg-beneficial text-white')}
            onClick={() => setOptionAddress?.(currentOption.optionAddress)}
          >
            <div className="line-clamp-1">
              <ApiTranslation>{currentOption.description || 'YES'}</ApiTranslation>
            </div>
            <div className="text-xs">
              <CurrencyView
                value={onChainBalance !== undefined ? onChainBalance : currentOption.balance}
                decimal={currentOption.decimal}
              />{' '}
              {t('shares')}
            </div>
          </button>
          <button
            type="button"
            disabled
            className={cn(
              'px-4 py-3 flex-1 rounded-2 min-h-12 font-semibold leading-none',
              'bg-destructive/20 text-destructive-foreground opacity-50 cursor-not-allowed'
            )}
          >
            <div>NO</div>
            <div className="text-xs">0 {t('shares')}</div>
          </button>
        </div>
      )}
      {/* Buy YES/NO buttons for multi-option market */}
      {type == 'buy' && isMultiOption && currentOption && (
        <div className="flex items-end justify-between flex-1 gap-3 pt-2">
          <button
            type="button"
            className={cn(
              'px-4 py-3 flex-1 rounded-2 min-h-12 font-semibold leading-none space-x-1',
              'hover:bg-beneficial hover:text-white bg-beneficial/20 text-beneficial-foreground',
              optionSide === 'yes' && 'bg-beneficial text-white'
            )}
            onClick={() => {
              setOptionSide?.('yes');
            }}
          >
            <span className="line-clamp-1">
              <ApiTranslation>{currentOption.description || 'YES'}</ApiTranslation>
            </span>
            <span>{Math.round((Number(currentOption.price) / Math.pow(10, currentOption.decimal)) * 100)}%</span>
          </button>
          <button
            type="button"
            className={cn(
              'px-4 py-3 flex-1 rounded-2 min-h-12 font-semibold leading-none space-x-1',
              'hover:bg-destructive hover:text-white bg-destructive/20 text-destructive-foreground',
              optionSide === 'no' && 'bg-destructive text-white'
            )}
            onClick={() => {
              setOptionSide?.('no');
            }}
          >
            <span>NO</span>
            <span>
              {Math.round(
                ((Math.pow(10, currentOption.decimal) - Number(currentOption.price)) / Math.pow(10, currentOption.decimal)) *
                  100
              )}
              %
            </span>
          </button>
        </div>
      )}
      {/* select yes no - for binary markets only */}
      {type == 'buy' && !isMultiOption && yes && no && (
        <div className="flex items-end justify-between flex-1 gap-3">
          <button
            type="button"
            className={cn(
              'px-4 py-3 flex-1 rounded-2 min-h-12 font-semibold leading-none space-x-1',
              'hover:bg-beneficial hover:text-white bg-beneficial/20 text-beneficial-foreground',
              optionAddress === yes.optionAddress && 'bg-beneficial text-white'
            )}
            onClick={() => {
              setOptionAddress?.(yes.optionAddress);
            }}
          >
            <ApiTranslation>{yes.description}</ApiTranslation>
            <CurrencyView isLoading={userPositions.isLoading} value={yes.price} decimal={yes.decimal} />
          </button>
          <button
            type="button"
            className={cn(
              'px-4 py-3 flex-1 rounded-2 min-h-12 font-semibold leading-none space-x-1',
              'hover:bg-destructive hover:text-white bg-destructive/20 text-destructive-foreground',
              optionAddress === no.optionAddress && 'bg-destructive text-white'
            )}
            onClick={() => {
              setOptionAddress?.(no.optionAddress);
            }}
          >
            <ApiTranslation>{no.description}</ApiTranslation>
            <CurrencyView isLoading={userPositions.isLoading} value={no.price} decimal={no.decimal} />
          </button>
        </div>
      )}
      {/* Sell YES/NO buttons for binary markets */}
      {type == 'sell' && !isMultiOption && yes && no && (
        <div className="flex items-end justify-between flex-1 gap-3">
          {(() => {
            const yesOpt = allOptionsWithBalance.find(
              o => o.optionAddress.toLowerCase() === yes.optionAddress.toLowerCase()
            );
            const noOpt = allOptionsWithBalance.find(o => o.optionAddress.toLowerCase() === no.optionAddress.toLowerCase());
            // Use on-chain balance for BOTH options (not just the selected one)
            const yesBalanceValue = yesOnChain.data !== undefined ? yesOnChain.data : (yesOpt?.balance ?? 0);
            const noBalanceValue = noOnChain.data !== undefined ? noOnChain.data : (noOpt?.balance ?? 0);
            const yesBalance = Number(yesBalanceValue);
            const noBalance = Number(noBalanceValue);
            return (
              <>
                <button
                  type="button"
                  disabled={yesBalance === 0}
                  className={cn(
                    'px-4 py-3 flex-1 rounded-2 min-h-12 font-semibold leading-none',
                    'hover:bg-beneficial hover:text-white bg-beneficial/20 text-beneficial-foreground',
                    optionAddress === yes.optionAddress && 'bg-beneficial text-white',
                    yesBalance === 0 && 'opacity-50 cursor-not-allowed'
                  )}
                  onClick={() => setOptionAddress?.(yes.optionAddress)}
                >
                  <div className="line-clamp-1">
                    <ApiTranslation>{yes.description || 'YES'}</ApiTranslation>
                  </div>
                  <div className="text-xs">
                    {yesOpt && <CurrencyView value={yesBalanceValue} decimal={yesOpt.decimal} />} {t('shares')}
                  </div>
                </button>
                <button
                  type="button"
                  disabled={noBalance === 0}
                  className={cn(
                    'px-4 py-3 flex-1 rounded-2 min-h-12 font-semibold leading-none',
                    'hover:bg-destructive hover:text-white bg-destructive/20 text-destructive-foreground',
                    optionAddress === no.optionAddress && 'bg-destructive text-white',
                    noBalance === 0 && 'opacity-50 cursor-not-allowed'
                  )}
                  onClick={() => setOptionAddress?.(no.optionAddress)}
                >
                  <div className="line-clamp-1">
                    <ApiTranslation>{no.description || 'NO'}</ApiTranslation>
                  </div>
                  <div className="text-xs">
                    {noOpt && <CurrencyView value={noBalanceValue} decimal={noOpt.decimal} />} {t('shares')}
                  </div>
                </button>
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
};
